<template>
    <drop-down
        :should-close="shouldCloseDropdown"
        @closeDropdown="(val) => (this.shouldCloseDropdown = val)"
        @closeOtherDropDown="closeOtherDropDown"
        :reduce="(inv) => inv.teamMemberInvoiceId"
    >
        <span v-if="isBulk" class="status bulk-status" slot="title" data-toggle="dropdown">
            CHANGE PAYMENT STATUS
        </span>
        <span
            v-else
            class="status"
            slot="title"
            data-toggle="dropdown"
            :class="[
                `payment-status-${invoiceDetails.status.toLowerCase().replace(/\s+/g, '')}`,
                { 'dropdown-menu-disabled': disabled },
                shouldShowMobileStyle ? 'mobile-status' : ''
            ]"
        >
            <span v-if="!shouldShowMobileStyle">{{ $t('inputs.paymentStatuses.' + invoiceDetails.status) }}</span>
            <span v-else>{{ invoiceDetails.status.charAt(0) }}</span>
        </span>
        <ul v-if="!disabled && !isTeamMember" :class="disabled ? 'dropdown-menu-disabled' : 'dropdown-menu'">
            <li v-for="(status, index) in statuses" :key="index">
                <a @click.stop="handleChangePaymentStatus(status.statusId)">
                    {{ status.statusValue }}
                </a>
            </li>
        </ul>
    </drop-down>
</template>

<script>
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { handleRequests, showErrorMessage } from '@/helpers';
import { PAYMENT_STATUS_CONSTANTS } from '@/utils/constants';
import { mapGetters } from 'vuex';

export default {
    name: 'PaymentStatusButton',
    mixins: [GeneralMixin],
    props: {
        invoiceDetails: {
            type: Object,
            default: () => undefined
        },
        isBulk: {
            type: Boolean,
            default: false
        },
        bulkTeamMemberInvoiceIds: {
            type: Array,
            default: () => []
        },
        disabled: {
            type: Boolean,
            default: false
        },
        showMobileStyle: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        ...mapGetters({
            isTeamMember: 'user/isTeamMember'
        }),
        isMobileView() {
            return window.innerWidth <= 768 || this.$root.isMobileOnly;
        },
        shouldShowMobileStyle() {
            return this.isMobileView && this.showMobileStyle;
        }
    },
    data() {
        return {
            oldStatus: null,
            invoiceDetailsValue: this.deepCopyInvDetails,
            statuses: this.getStatuses(PAYMENT_STATUS_CONSTANTS),
            shouldCloseDropdown: false
        };
    },
    mounted() {
        if (!this.isBulk) 
            this.oldStatus = this.invoiceDetails.status;
    },
    methods: {
        getStatuses(arr) {
            const stats = arr.map((x) => ({
                statusId: x,
                statusValue: this.$t(`inputs.paymentStatuses.${x}`)
            }));

            return stats;
        },
        async handleChangePaymentStatus(newStatus) {
            this.shouldCloseDropdown = true;
            if (this.isBulk) {
                this.handleBulk(newStatus);
                return;
            }

            if (this.invoiceDetails.status !== newStatus) {
                this.$emit('statusUpdated', {
                    teamMemberInvoiceId: this.invoiceDetails.teamMemberInvoiceId,
                    status: newStatus
                });
                const payload = {
                    method: 'post',
                    data: { newStatus }
                };

                // to toggle the context menu
                this.$nextTick(() => {
                    this.shouldCloseDropdown = false;
                });

                try {
                    const api = `/api/team-member-invoices/${this.invoiceDetails.teamMemberInvoiceId}/change-status`;
                    await handleRequests(api, payload);

                    this.$notify({
                        message: `Invoice status was changed to ${this.$t(`inputs.paymentStatuses.${newStatus}`)}!`,
                        type: 'success'
                    });
                } catch (e) {
                    const message = 'Cannot change invoice status.';
                    showErrorMessage(this, message, e);
                    this.$emit('statusUpdated', {
                        teamMemberInvoiceId: this.invoiceDetails.teamMemberInvoiceId,
                        status: this.oldStatus
                    });
                }
            } else {
                const message = 'Cannot change status to the same status';
                this.$notify({
                    message,
                    type: 'danger'
                });
            }
        },
        async handleBulk(newStatus) {
            this.$_handleLoaderState(true, 'UPDATING...');
            try {
                const payload = {
                    method: 'post',
                    data: {
                        invoices: this.bulkTeamMemberInvoiceIds,
                        newStatus
                    }
                };
                const api = `/api/team-member-invoices/bulk/change-status`;
                await handleRequests(api, payload);
                this.$emit('invoiceBulkStatusUpdate', true);

                this.$notify({
                    message: `Successfully updated the invoices to ${this.$t(`inputs.paymentStatuses.${newStatus}`)}!`,
                    type: 'success'
                });
                this.$_handleLoaderState(false);
            } catch (e) {
                const message = 'Cannot change invoice status.';
                showErrorMessage(this, message, e);
                this.$emit('invoiceBulkStatusUpdate', false);
                this.$_handleLoaderState(false);
            }
        },
        closeOtherDropDown() {
            const dropdowns = document.querySelectorAll('.open');
            dropdowns.forEach((dropdown) => dropdown.classList.remove('open'));
        }
    }
};
</script>

<style lang="scss" scoped>
.status {
    color: #fff !important;
    min-width: 140px;
    text-align: center;
    display: block;
    line-height: 31px;
    font-weight: 400;
    max-width: 140px;
    cursor: pointer;
}

::v-deep .dropdown-menu {
    li {
        cursor: pointer !important;
    }
}

.dropdown-menu-disabled {
    cursor: default !important;
}

.bulk-status {
    width: 100%;
    min-width: initial;
    max-width: 100%;
    background-color: #00bcd4;
}

.mobile-status {
    min-width: 32px !important;
    max-width: 32px !important;
    width: 32px !important;
    height: 32px !important;
    border-radius: 50% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    padding: 0 !important;
    font-weight: 600 !important;
    font-size: 14px !important;
}
</style>
